import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchCasaMapaAdminListingHealth } from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale, useCurrency } from "../../../intl";
import CasaMapaListingRepairDrawer from "./CasaMapaListingRepairDrawer";
import "../../../styles/casamapaAdminPortal.css";
import "../../../styles/casamapaListingHealthDashboard.css";

const COPY = "realEstate.casaMapa.admin.listings";
const QUEUE_PAGE_SIZE = 20;

function parseIssueFilters(searchParams) {
  const raw = searchParams.get("issues") || searchParams.get("issue") || "";
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function IssueTypeFilterMenu({ issueOptions, selectedIssues, onToggle, onClear, t }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const buttonLabel =
    selectedIssues.length === 0
      ? t(`${COPY}.issueTypeFilterAll`)
      : selectedIssues.length === 1
        ? t(`${COPY}.issues.${selectedIssues[0]}`)
        : t(`${COPY}.issueTypeFilterSelected`, { count: selectedIssues.length });

  return (
    <div className="cm-listing-health__issue-filter" ref={rootRef}>
      <button
        type="button"
        className="cm-listing-health__issue-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{t(`${COPY}.issueTypeFilterLabel`)}</span>
        <strong>{buttonLabel}</strong>
        <span className="cm-listing-health__issue-filter-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <div className="cm-listing-health__issue-filter-menu" role="listbox" aria-multiselectable="true">
          <div className="cm-listing-health__issue-filter-menu-head">
            <span>{t(`${COPY}.issueTypeFilterLabel`)}</span>
            {selectedIssues.length ? (
              <button type="button" className="cm-listing-health__issue-filter-clear" onClick={onClear}>
                {t(`${COPY}.clearFilter`)}
              </button>
            ) : null}
          </div>
          {issueOptions.map((issue) => {
            const checked = selectedIssues.includes(issue.id);
            return (
              <label key={issue.id} className="cm-listing-health__issue-filter-option">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(issue.id)}
                />
                <span className="cm-listing-health__issue-filter-option-copy">
                  <span>{t(`${COPY}.issues.${issue.id}`)}</span>
                  <small>{issue.sampleCount ?? 0}</small>
                </span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function listingLocation(listing) {
  const parts = [listing.neighborhood, listing.municipality].filter(Boolean);
  return parts.length ? parts.join(", ") : listing.title;
}

function KpiCard({ label, value, hint, tone = "slate" }) {
  return (
    <article className={`cm-listing-health__kpi cm-listing-health__kpi--${tone}`}>
      <span className="cm-listing-health__kpi-label">{label}</span>
      <strong className="cm-listing-health__kpi-value">{value ?? "—"}</strong>
      {hint ? <small className="cm-listing-health__kpi-hint">{hint}</small> : null}
    </article>
  );
}

function DistributionBar({ label, value, max, tone = "blue" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cm-listing-health__bar-row">
      <div className="cm-listing-health__bar-head">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="cm-listing-health__bar-track" aria-hidden="true">
        <div className={`cm-listing-health__bar-fill cm-listing-health__bar-fill--${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function CasaMapaAdminListingsPage() {
  const { path, clientViewId } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const { formatMoney } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [repairListingId, setRepairListingId] = useState(null);

  const agencySlug = searchParams.get("agencySlug") || "";
  const listingType = searchParams.get("listingType") || "";
  const selectedIssues = useMemo(() => parseIssueFilters(searchParams), [searchParams]);
  const search = searchParams.get("q") || "";
  const queuePage = Math.max(1, Number(searchParams.get("page")) || 1);

  const loadDashboard = useCallback(async () => {
    if (!clientViewId) return;
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaAdminListingHealth(clientViewId, {
        agencySlug: agencySlug || undefined,
        listingType: listingType || undefined,
        issue: selectedIssues.length ? selectedIssues.join(",") : undefined,
        q: search.trim() || undefined,
        page: queuePage,
      });
      setDashboard(result.dashboard || null);
    } catch (err) {
      setError(err.message || t("realEstate.casaMapa.admin.errors.loadListings"));
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [agencySlug, clientViewId, listingType, queuePage, search, selectedIssues, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updateFilter = (key, value, { resetPage = key !== "page" } = {}) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const updateIssueFilters = (nextIssues) => {
    const next = new URLSearchParams(searchParams);
    next.delete("issue");
    if (nextIssues.length) next.set("issues", nextIssues.join(","));
    else next.delete("issues");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const toggleIssue = (issueId) => {
    const nextIssues = selectedIssues.includes(issueId)
      ? selectedIssues.filter((id) => id !== issueId)
      : [...selectedIssues, issueId];
    updateIssueFilters(nextIssues);
  };

  const summary = dashboard?.summary;
  const issues = dashboard?.issues || [];
  const listings = dashboard?.listings || [];
  const queue = dashboard?.queue || { total: listings.length, page: queuePage, pageSize: QUEUE_PAGE_SIZE, totalPages: 1 };
  const agencies = dashboard?.agencies || [];
  const agencyOptions = dashboard?.agencyOptions || [];

  const freshnessRows = useMemo(() => {
    const buckets = dashboard?.freshness || {};
    return Object.entries(buckets).map(([key, value]) => ({
      key,
      label: t(`${COPY}.freshness.${key}`),
      value,
      tone: key === "stale" ? "red" : key === "aging" ? "amber" : key === "recently_confirmed" ? "green" : "blue",
    }));
  }, [dashboard?.freshness, t]);

  const completenessRows = useMemo(() => {
    const buckets = dashboard?.completeness || {};
    return Object.entries(buckets).map(([key, value]) => ({
      key,
      label: t(`${COPY}.completeness.${key}`),
      value,
      tone: key === "high" ? "green" : key === "limited" ? "red" : "amber",
    }));
  }, [dashboard?.completeness, t]);

  const maxFreshness = Math.max(...freshnessRows.map((row) => row.value), 1);
  const maxCompleteness = Math.max(...completenessRows.map((row) => row.value), 1);
  const visibleIssues = useMemo(
    () => issues.filter((item) => (item.sampleCount ?? 0) > 0),
    [issues]
  );
  const hasActiveFilters = Boolean(selectedIssues.length || agencySlug || listingType || search);

  if (!clientViewId) {
    return <p className="muted">Select a CasaMapa client view to continue.</p>;
  }

  return (
    <section className="casamapa-admin-page cm-listing-health">
      <header className="casamapa-admin-page__header cm-listing-health__header">
        <div>
          <div className="eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</div>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="muted">{t(`${COPY}.subtitle`)}</p>
          {summary?.sampleSize ? (
            <p className="cm-listing-health__sample-note">
              {t(`${COPY}.sampleNote`, { sampleSize: summary.sampleSize.toLocaleString(locale) })}
            </p>
          ) : null}
        </div>
        <div className="cm-listing-health__header-actions">
          <button type="button" className="midpoint-share-button" onClick={loadDashboard}>
            {t(`${COPY}.refresh`)}
          </button>
          <Link className="casamapa-admin-link-button" to={path("agencies")}>
            {t("realEstate.casaMapa.admin.nav.agencies")}
          </Link>
        </div>
      </header>

      <div className="cm-listing-health__filters">
        <div className="cm-listing-health__filters-bar">
          <label className="cm-listing-health__search">
            <span className="cm-listing-health__search-icon" aria-hidden="true">
              ⌕
            </span>
            <input
              type="search"
              className="cm-listing-health__search-input"
              placeholder={t(`${COPY}.searchPlaceholder`)}
              value={search}
              onChange={(event) => updateFilter("q", event.target.value)}
            />
          </label>

          <div className="cm-listing-health__filter-group">
            <label className="cm-listing-health__filter-field">
              <span className="cm-listing-health__filter-label">{t(`${COPY}.typeFilterLabel`)}</span>
              <select
                className="cm-listing-health__select"
                value={listingType}
                onChange={(event) => updateFilter("listingType", event.target.value)}
                aria-label={t(`${COPY}.typeFilterLabel`)}
              >
                <option value="">{t("realEstate.casaMapa.admin.listingType.all")}</option>
                <option value="sale">{t("realEstate.casaMapa.admin.listingType.sale")}</option>
                <option value="rent">{t("realEstate.casaMapa.admin.listingType.rent")}</option>
              </select>
            </label>

            <label className="cm-listing-health__filter-field">
              <span className="cm-listing-health__filter-label">{t(`${COPY}.agencyFilterLabel`)}</span>
              <select
                className="cm-listing-health__select"
                value={agencySlug}
                onChange={(event) => updateFilter("agencySlug", event.target.value)}
                aria-label={t(`${COPY}.agencyFilterLabel`)}
              >
                <option value="">{t(`${COPY}.allAgencies`)}</option>
                {agencyOptions.map((agency) => (
                  <option key={agency.slug} value={agency.slug}>
                    {agency.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {hasActiveFilters ? (
          <div className="cm-listing-health__active-filters">
            {search ? (
              <button
                type="button"
                className="cm-listing-health__filter-chip"
                onClick={() => updateFilter("q", "")}
              >
                {t(`${COPY}.activeFilters.search`, { value: search })}
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
            {listingType ? (
              <button
                type="button"
                className="cm-listing-health__filter-chip"
                onClick={() => updateFilter("listingType", "")}
              >
                {t(`${COPY}.activeFilters.type`, {
                  value: t(`realEstate.casaMapa.admin.listingType.${listingType}`),
                })}
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
            {agencySlug ? (
              <button
                type="button"
                className="cm-listing-health__filter-chip"
                onClick={() => updateFilter("agencySlug", "")}
              >
                {t(`${COPY}.activeFilters.agency`, {
                  value: agencyOptions.find((agency) => agency.slug === agencySlug)?.name || agencySlug,
                })}
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
            {selectedIssues.map((issueId) => (
              <button
                key={issueId}
                type="button"
                className="cm-listing-health__filter-chip"
                onClick={() => toggleIssue(issueId)}
              >
                {t(`${COPY}.activeFilters.issue`, { value: t(`${COPY}.issues.${issueId}`) })}
                <span aria-hidden="true">×</span>
              </button>
            ))}
            <button
              type="button"
              className="cm-listing-health__clear-all"
              onClick={() => setSearchParams({}, { replace: true })}
            >
              {t(`${COPY}.clearFilter`)}
            </button>
          </div>
        ) : null}
      </div>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {!loading && summary ? (
        <>
          <div className="cm-listing-health__kpi-grid">
            <KpiCard
              label={t(`${COPY}.kpi.total`)}
              value={summary.totalListings?.toLocaleString(locale)}
              tone="blue"
            />
            <KpiCard
              label={t(`${COPY}.kpi.sale`)}
              value={summary.saleListings?.toLocaleString(locale)}
              tone="slate"
            />
            <KpiCard
              label={t(`${COPY}.kpi.rent`)}
              value={summary.rentListings?.toLocaleString(locale)}
              tone="slate"
            />
            <KpiCard
              label={t(`${COPY}.kpi.withIssues`)}
              value={summary.listingsWithIssues?.toLocaleString(locale)}
              hint={`${summary.healthyPercent ?? 0}% ${t(`${COPY}.kpi.healthy`).toLowerCase()}`}
              tone="amber"
            />
            <KpiCard
              label={t(`${COPY}.kpi.avgCompleteness`)}
              value={summary.averageCompleteness != null ? `${summary.averageCompleteness}%` : "—"}
              tone="green"
            />
            <KpiCard
              label={t(`${COPY}.kpi.conflicts`)}
              value={dashboard?.canonical?.conflictListings?.toLocaleString(locale) ?? "0"}
              tone="red"
            />
          </div>

          {dashboard?.mailingAddress?.classified ? (
            <section className="cm-listing-health__panel cm-listing-health__mailing">
              <div className="cm-listing-health__panel-head">
                <h2>{t(`${COPY}.sections.mailingAddress`)}</h2>
                <span className="muted">
                  {t(`${COPY}.mailingAddress.classified`, {
                    count: (dashboard.mailingAddress.classified || 0).toLocaleString(locale),
                  })}
                </span>
              </div>
              <p className="muted cm-listing-health__mailing-note">{t(`${COPY}.mailingAddress.note`)}</p>
              <div className="cm-listing-health__mailing-grid">
                {[
                  ["classA", dashboard.mailingAddress.class_a, "green"],
                  ["classB", dashboard.mailingAddress.class_b, "blue"],
                  ["classC", dashboard.mailingAddress.class_c, "amber"],
                  ["classD", dashboard.mailingAddress.class_d, "slate"],
                  ["classE", dashboard.mailingAddress.class_e, "red"],
                  ["official", dashboard.mailingAddress.officialSnaps, "green"],
                  ["review", dashboard.mailingAddress.needsReview, "amber"],
                  ["unsnappedA", dashboard.mailingAddress.classAUnsnapped, "slate"],
                  ["geocodeReview", dashboard.mailingAddress.geocodeNeedsReview, "amber"],
                ].map(([key, value, tone]) => (
                  <div key={key} className={`cm-listing-health__mailing-cell cm-listing-health__mailing-cell--${tone}`}>
                    <strong>{(value || 0).toLocaleString(locale)}</strong>
                    <span>{t(`${COPY}.mailingAddress.${key}`)}</span>
                  </div>
                ))}
              </div>
              {dashboard.mailingAddress.officialMunicipalities?.length ? (
                <div className="cm-listing-health__mailing-munis">
                  <h3>{t(`${COPY}.mailingAddress.officialMunicipalities`)}</h3>
                  <ul>
                    {dashboard.mailingAddress.officialMunicipalities.map((row) => (
                      <li key={row.dane_code || row.municipality}>
                        <span>{row.municipality}</span>
                        <strong>{(row.listings || 0).toLocaleString(locale)}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {dashboard.mailingAddress.reviewQueue?.length ? (
                <div className="cm-listing-health__mailing-review">
                  <h3>{t(`${COPY}.mailingAddress.reviewTitle`)}</h3>
                  <ul>
                    {dashboard.mailingAddress.reviewQueue.map((item) => (
                      <li key={item.canonicalListingId}>
                        <Link to={path(`listings/${item.canonicalListingId}`)}>
                          {item.title}
                        </Link>
                        <span>
                          {t(`${COPY}.mailingAddress.reviewReasons.${item.reviewReason || "other"}`)}
                          {item.addressClass ? ` · ${item.addressClass}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>
          ) : null}

          <div className="cm-listing-health__main-grid">
            <section className="cm-listing-health__panel">
              <div className="cm-listing-health__panel-head">
                <h2>{t(`${COPY}.sections.issues`)}</h2>
              </div>
              <div className="cm-listing-health__issue-grid">
                {visibleIssues.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`cm-listing-health__issue-card cm-listing-health__issue-card--${item.severity}${
                      selectedIssues.includes(item.id) ? " is-active" : ""
                    }`}
                    onClick={() => toggleIssue(item.id)}
                  >
                    <div className="cm-listing-health__issue-top">
                      <span className={`cm-listing-health__severity cm-listing-health__severity--${item.severity}`}>
                        {t(`${COPY}.severity.${item.severity}`)}
                      </span>
                      <strong>{item.sampleCount}</strong>
                    </div>
                    <h3>{t(`${COPY}.issues.${item.id}`)}</h3>
                    <p>{t(`${COPY}.issueHints.${item.id}`)}</p>
                    <div className="cm-listing-health__issue-meta">
                      <span>{item.percentOfSample}%</span>
                      {item.estimatedCount ? (
                        <span>{t(`${COPY}.estimatedCount`, { count: item.estimatedCount.toLocaleString(locale) })}</span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
              {!visibleIssues.length ? (
                <p className="muted cm-listing-health__issues-empty">{t(`${COPY}.noIssuesInSample`)}</p>
              ) : null}
            </section>

            <div className="cm-listing-health__side-stack">
              <section className="cm-listing-health__panel">
                <div className="cm-listing-health__panel-head">
                  <h2>{t(`${COPY}.sections.freshness`)}</h2>
                </div>
                <div className="cm-listing-health__bars">
                  {freshnessRows.map((row) => (
                    <DistributionBar key={row.key} label={row.label} value={row.value} max={maxFreshness} tone={row.tone} />
                  ))}
                </div>
              </section>

              <section className="cm-listing-health__panel">
                <div className="cm-listing-health__panel-head">
                  <h2>{t(`${COPY}.sections.completeness`)}</h2>
                </div>
                <div className="cm-listing-health__bars">
                  {completenessRows.map((row) => (
                    <DistributionBar key={row.key} label={row.label} value={row.value} max={maxCompleteness} tone={row.tone} />
                  ))}
                </div>
              </section>
            </div>
          </div>

          {agencies.length ? (
            <section className="cm-listing-health__panel">
              <div className="cm-listing-health__panel-head">
                <h2>{t(`${COPY}.sections.agencies`)}</h2>
              </div>
              <div className="cm-listing-health__agency-grid">
                {agencies.slice(0, 6).map((agency) => (
                  <button
                    key={agency.slug || agency.name}
                    type="button"
                    className={`cm-listing-health__agency-card${agencySlug === agency.slug ? " is-active" : ""}`}
                    onClick={() => updateFilter("agencySlug", agency.slug || "")}
                  >
                    <strong>{agency.name}</strong>
                    <span>{t(`${COPY}.agencyIssues`, { count: agency.listingsWithIssues })}</span>
                    {agency.topIssue ? (
                      <small>
                        {t(`${COPY}.topIssue`, { issue: t(`${COPY}.issues.${agency.topIssue}`) })}
                      </small>
                    ) : null}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <section className="cm-listing-health__panel">
            <div className="cm-listing-health__panel-head cm-listing-health__panel-head--queue">
              <div className="cm-listing-health__queue-head">
                <h2>{t(`${COPY}.sections.queue`)}</h2>
                <span className="cm-listing-health__queue-count">
                  {t(`${COPY}.queueCount`, { count: (queue.total ?? 0).toLocaleString(locale) })}
                </span>
              </div>
              <IssueTypeFilterMenu
                issueOptions={issues}
                selectedIssues={selectedIssues}
                onToggle={toggleIssue}
                onClear={() => updateIssueFilters([])}
                t={t}
              />
            </div>

            {listings.length ? (
              <div className="casamapa-admin-table-wrap">
                <table className="casamapa-admin-table cm-listing-health__table">
                  <thead>
                    <tr>
                      <th>{t(`${COPY}.table.listing`)}</th>
                      <th>{t(`${COPY}.table.agency`)}</th>
                      <th>{t(`${COPY}.table.issues`)}</th>
                      <th>{t(`${COPY}.table.completeness`)}</th>
                      <th>{t(`${COPY}.table.freshness`)}</th>
                      <th>{t(`${COPY}.table.photos`)}</th>
                      <th>{t(`${COPY}.table.public`)}</th>
                      <th>{t(`${COPY}.table.actions`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((listing) => (
                      <tr key={listing.id}>
                        <td>
                          <div className="casamapa-admin-listing-cell">
                            {listing.photoUrl ? (
                              <img src={listing.photoUrl} alt="" className="casamapa-admin-listing-cell__photo" />
                            ) : (
                              <span className="casamapa-admin-listing-cell__placeholder" aria-hidden="true">
                                ⌂
                              </span>
                            )}
                            <div>
                              <Link
                                to={path(`listings/${listing.id}`)}
                                className="casamapa-admin-listing-link"
                              >
                                <strong>{listing.title}</strong>
                              </Link>
                              <div className="muted">{listingLocation(listing)}</div>
                              <div className="muted">
                                {listing.priceCop != null ? formatMoney(listing.priceCop) : "—"} · {listing.listingType || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{listing.agencyName || listing.sourceKey || "—"}</td>
                        <td>
                          <div className="cm-listing-health__issue-tags">
                            {listing.issueIds.map((issueId) => (
                              <span
                                key={issueId}
                                className={`cm-listing-health__issue-tag cm-listing-health__issue-tag--${
                                  selectedIssues.includes(issueId) ? "active" : "default"
                                }`}
                              >
                                {t(`${COPY}.issues.${issueId}`)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{listing.completenessScore != null ? `${listing.completenessScore}%` : "—"}</td>
                        <td>{t(`${COPY}.freshness.${listing.freshnessStatus || "unknown"}`)}</td>
                        <td>{listing.imageCount ?? 0}</td>
                        <td>
                          {listing.publicUrl ? (
                            <a href={listing.publicUrl} target="_blank" rel="noreferrer">
                              {t("realEstate.casaMapa.admin.viewPublic")}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div className="cm-listing-health__row-actions">
                            <Link
                              className="cm-listing-health__detail-link"
                              to={path(`listings/${listing.id}`)}
                            >
                              {t(`${COPY}.viewDetail`)}
                            </Link>
                            <button
                              type="button"
                              className="cm-listing-health__fix-button"
                              onClick={() => setRepairListingId(listing.id)}
                            >
                              {t(`${COPY}.repairAction`)}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">{t(`${COPY}.empty`)}</p>
            )}

            {queue.totalPages > 1 ? (
              <div className="cm-listing-health__pagination">
                <button
                  type="button"
                  className="partner-dashboard-btn"
                  disabled={queue.page <= 1}
                  onClick={() => updateFilter("page", String(queue.page - 1), { resetPage: false })}
                >
                  {t(`${COPY}.pagination.previous`)}
                </button>
                <span className="muted">
                  {t(`${COPY}.pagination.page`, { page: queue.page, totalPages: queue.totalPages })}
                </span>
                <button
                  type="button"
                  className="partner-dashboard-btn"
                  disabled={queue.page >= queue.totalPages}
                  onClick={() => updateFilter("page", String(queue.page + 1), { resetPage: false })}
                >
                  {t(`${COPY}.pagination.next`)}
                </button>
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {repairListingId ? (
        <CasaMapaListingRepairDrawer
          clientViewId={clientViewId}
          listingId={repairListingId}
          onClose={() => setRepairListingId(null)}
          onSaved={() => loadDashboard()}
        />
      ) : null}
    </section>
  );
}
